import { useState, useEffect, useCallback } from 'react';
import { 
  adminUserService, 
  AdminUser, 
  AdminUserFilters, 
  AdminUserStats,
  CreateAdminUserRequest,
  UpdateAdminUserRequest,
  AdminUserResponse
} from '../services/adminUserService';

export interface UseAdminUsersReturn {
  adminUsers: AdminUser[];
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  } | null;
  stats: AdminUserStats | null;
  fetchAdminUsers: (filters?: AdminUserFilters) => Promise<void>;
  refreshData: () => Promise<void>;
  updateFilters: (newFilters: Partial<AdminUserFilters>) => void;
  createAdminUser: (userData: CreateAdminUserRequest) => Promise<AdminUserResponse>;
  updateAdminUser: (id: string, userData: UpdateAdminUserRequest) => Promise<AdminUserResponse>;
  deleteAdminUser: (id: string) => Promise<{ success: boolean; message: string }>;
  updateUserStatus: (id: string, status: 'enabled' | 'disabled') => Promise<{
    success: boolean;
    message: string;
    data: {
      id: string;
      fullName: string;
      email: string;
      role: string;
      previousStatus: string;
      newStatus: string;
      updatedAt: string;
    };
  }>;
  changeUserPassword: (id: string, currentPassword: string, newPassword: string) => Promise<{
    success: boolean;
    message: string;
  }>;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
}

export function useAdminUsers(initialFilters: AdminUserFilters = {}): UseAdminUsersReturn {
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pagination, setPagination] = useState<{
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  } | null>(null);
  const [stats, setStats] = useState<AdminUserStats | null>(null);
  const [filters, setFilters] = useState<AdminUserFilters>({
    page: 1,
    limit: 10,
    ...initialFilters,
  });

  const fetchAdminUsers = useCallback(async (newFilters?: AdminUserFilters) => {
    setLoading(true);
    setError(null);

    try {
      const currentFilters = newFilters || filters;
      const response = await adminUserService.getAdminUsers(currentFilters);
      
      
      // Handle the API response structure
      let dataArray: AdminUser[] = [];
      let paginationData = null;
      
      if (response && typeof response === 'object') {
        // Handle the new API response structure
        if (response.success && response.data && typeof response.data === 'object' && 'users' in response.data) {
          const apiData = response.data as any;
          dataArray = Array.isArray(apiData.users) ? apiData.users : [];
          
          // Map the pagination structure from the API response
          if (apiData.pagination) {
            paginationData = {
              page: apiData.pagination.currentPage || 1,
              limit: apiData.pagination.limit || 10,
              total: apiData.pagination.totalCount || 0,
              totalPages: apiData.pagination.totalPages || 1
            };
          }
        }
        // Handle direct array response (if API returns array directly)
        else if (Array.isArray(response)) {
          dataArray = response;
          paginationData = {
            page: 1,
            limit: response.length,
            total: response.length,
            totalPages: 1
          };
        }
        // Fallback for other response structures
        else if (response.data !== undefined) {
          dataArray = Array.isArray(response.data) ? response.data : [];
          paginationData = (response as any).pagination || {
            page: 1,
            limit: 10,
            total: 0,
            totalPages: 0
          };
        }
        else {
          dataArray = [];
        }
      }
      
      
      setAdminUsers(dataArray);
      if (paginationData) {
        setPagination(paginationData);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      console.error('Error fetching admin users:', err);
      // Set empty array as fallback
      setAdminUsers([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchStats = useCallback(async () => {
    try {
      const statsData = await adminUserService.getAdminUserStats();
      setStats(statsData);
    } catch (err) {
      console.error('Error fetching admin user stats:', err);
      // Don't set error for stats as it's not critical
    }
  }, []);

  const refreshData = useCallback(async () => {
    await Promise.all([
      fetchAdminUsers(),
      fetchStats(),
    ]);
  }, [fetchAdminUsers, fetchStats]);

  const updateFilters = useCallback((newFilters: Partial<AdminUserFilters>) => {
    setFilters(prev => {
      const updated = { ...prev, ...newFilters };
      return updated;
    });
  }, []);

  const createAdminUser = useCallback(async (userData: CreateAdminUserRequest): Promise<AdminUserResponse> => {
    setIsCreating(true);
    setError(null);

    try {
      const response = await adminUserService.createAdminUser(userData);
      
      // Refresh the data to get the new user
      await refreshData();
      
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create admin user';
      setError(errorMessage);
      throw err;
    } finally {
      setIsCreating(false);
    }
  }, [refreshData]);

  const updateAdminUser = useCallback(async (id: string, userData: UpdateAdminUserRequest): Promise<AdminUserResponse> => {
    setIsUpdating(true);
    setError(null);

    try {
      const response = await adminUserService.updateAdminUser(id, userData);
      
      // Refresh the data to get updated user details
      await refreshData();
      
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update admin user';
      setError(errorMessage);
      throw err;
    } finally {
      setIsUpdating(false);
    }
  }, [refreshData]);

  const deleteAdminUser = useCallback(async (id: string): Promise<{ success: boolean; message: string }> => {
    setIsDeleting(true);
    setError(null);

    try {
      const response = await adminUserService.deleteAdminUser(id);
      
      // Refresh the data to get updated user list
      await refreshData();
      
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete admin user';
      setError(errorMessage);
      throw err;
    } finally {
      setIsDeleting(false);
    }
  }, [refreshData]);

  const updateUserStatus = useCallback(async (id: string, status: 'enabled' | 'disabled') => {
    setIsUpdating(true);
    setError(null);

    try {
      const response = await adminUserService.updateUserStatus(id, status);
      
      // Refresh the data to get updated user list
      await refreshData();
      
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update user status';
      setError(errorMessage);
      throw err;
    } finally {
      setIsUpdating(false);
    }
  }, [refreshData]);

  const changeUserPassword = useCallback(async (id: string, currentPassword: string, newPassword: string) => {
    setIsUpdating(true);
    setError(null);

    try {
      const response = await adminUserService.changeUserPassword(id, currentPassword, newPassword);
      
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to change password';
      setError(errorMessage);
      throw err;
    } finally {
      setIsUpdating(false);
    }
  }, []);

  // Fetch data when filters change
  useEffect(() => {
    fetchAdminUsers();
  }, [fetchAdminUsers]);

  // Fetch stats on mount
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return {
    adminUsers,
    loading,
    error,
    pagination,
    stats,
    fetchAdminUsers,
    refreshData,
    updateFilters,
    createAdminUser,
    updateAdminUser,
    deleteAdminUser,
    updateUserStatus,
    changeUserPassword,
    isCreating,
    isUpdating,
    isDeleting,
  };
}